import React, { useState, useEffect } from 'react';
import { analytics } from '../utils/analytics';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  Grid,
  Link,
  InputAdornment,
  IconButton,
  Divider,
  CircularProgress,
  Alert,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import HomeIcon from '@mui/icons-material/Home';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import MuiAlert from '@mui/material/Alert';
import { Snackbar } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import Header from '../components/Header';
import DotGridBackground from '../components/DotGridBackground';
import { OAuthButtonGroup } from '../components/OAuthButton';
import useOAuth from '../hooks/useOAuth';
import api from '../services/api'; // Use centralized API service

const Login = () => {
  const theme = useTheme();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'error'
  });
  const navigate = useNavigate();
  const { login } = useAuth();
  const { availableProviders, initiateOAuth, loading: oauthLoading, error: oauthError } = useOAuth();

  const handleOAuthLogin = (provider) => {
    if (oauthLoading) return;
    analytics.oauthLoginClick(provider);
    try {
      initiateOAuth(provider);
    } catch (error) {
      setSnackbar({
        open: true,
        message: `Failed to initiate ${provider} login`,
        severity: 'error'
      });
    }
  };

  // Show OAuth error if any
  useEffect(() => {
    if (oauthError) {
      setSnackbar({
        open: true,
        message: oauthError,
        severity: 'error'
      });
    }
  }, [oauthError]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setErrors({});
    
    const newErrors = {};
    if (!email) newErrors.email = 'Email is required';
    if (!password) newErrors.password = 'Password is required';
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      setIsSubmitting(true);
      const response = await api.post('/auth/signin', { email, password });
      const data = response.data || response; // Handle both new and legacy formats

      if (data.token) {
        // Debug: Log the user data from backend
        // console.log('🔍 Backend login response user data:', data.user);
        // console.log('🔍 User roles:', data.user?.roles);
        // console.log('🔍 User primary_role:', data.user?.primary_role);
        
        // Add permissions to user object before passing to login
        const userWithPermissions = {
          ...data.user,
          permissions: data.permissions || []
        };
        
        // Pass token and user in correct order as expected by AuthContext
        await login(userWithPermissions, data.token);  // Now includes permissions
        analytics.login('email');
        
        setSnackbar({
          open: true,
          message: 'Login successful! Redirecting...',
          severity: 'success'
        });

        // Navigate to dashboard after successful login
        setTimeout(() => navigate('/dashboard'), 1500);
      }
    } catch (err) {
      const isInvalidCredentials = err?.statusCode === 401;
      const errorMessage = isInvalidCredentials
        ? 'Invalid email or password. Please try again.'
        : (err.message || 'Login failed');

      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });

      if (errorMessage.toLowerCase().includes('email')) {
        setErrors(prev => ({ ...prev, email: errorMessage }));
      } else if (errorMessage.toLowerCase().includes('password')) {
        setErrors(prev => ({ ...prev, password: errorMessage }));
      } else {
        setErrors(prev => ({ ...prev, general: errorMessage }));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  return (
    <Box 
      sx={{ 
        minHeight: '100dvh',
        background: theme.modern.backgrounds.gradient,
        position: 'relative',
        overflowX: 'hidden',
        width: '100%',
      }}
    >
      <DotGridBackground />
      <Header />
      <Box sx={{ pt: { xs: 8, sm: 9 }, px: { xs: 0, sm: 2 }, position: 'relative', zIndex: 2 }}>
        <Container maxWidth="sm" sx={{ py: { xs: 2, sm: 8 }, px: { xs: 2, sm: 3 }, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <Box sx={{ mb: { xs: 2, sm: 4 }, textAlign: 'center' }}>
            <RouterLink to="/" style={{ textDecoration: 'none' }}>
              <Typography
                variant="h3"
                component="div"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 1,
                  ...theme.modern.utils.gradientText('primary'),
                  mb: 1,
                  fontSize: { xs: '1.75rem', sm: '2.5rem', md: '3rem' },
                }}
              >
                {/* <HomeIcon sx={{ color: theme.modern.colors.modern.mainColor[500] }} /> */}
                Welcome Back
              </Typography>
            </RouterLink>
            
            <Typography 
              variant="body1" 
              sx={{ 
                ...theme.modern.typography.modernSubtext 
              }}
            >
              Log in to your account to continue
            </Typography>
          </Box>

          <Paper
            variant="modern"
            elevation={0}
            sx={{
              p: { xs: 2, sm: 4 },
              maxWidth: '100%',
              overflow: 'hidden',
            }}
          >
            <form onSubmit={handleLogin}>
              <Grid container spacing={3}>
                {/* OAuth Providers Section */}
                {availableProviders.length > 0 && (
                  <>
                    <Grid size={12}>
                      <OAuthButtonGroup
                        providers={availableProviders}
                        onProviderSelect={handleOAuthLogin}
                        disabled={isSubmitting || oauthLoading}
                        orientation="vertical"
                      />
                    </Grid>

                    <Grid size={12}>
                      <Box sx={{ display: 'flex', alignItems: 'center', my: 1 }}>
                        <Divider sx={{ flex: 1 }} />
                        <Typography variant="body2" sx={{ px: 2, color: '#64748b' }}>
                          OR
                        </Typography>
                        <Divider sx={{ flex: 1 }} />
                      </Box>
                    </Grid>
                  </>
                )}

                {/* Fallback: Show message if no OAuth providers are available */}
                {availableProviders.length === 0 && (
                  <Grid size={12}>
                    <Alert severity="info" sx={{ mb: 2 }}>
                      Social login is currently unavailable. Please use email and password to sign in.
                    </Alert>
                  </Grid>
                )}

                <Grid size={12}>
                  <TextField
                    fullWidth
                    label="Email Address"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    error={!!errors.email}
                    helperText={errors.email}
                    variant="outlined"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '0.5rem',
                      },
                    }}
                  />
                </Grid>
                <Grid size={12}>
                  <TextField
                    fullWidth
                    label="Password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    error={!!errors.password}
                    helperText={errors.password}
                    variant="outlined"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                          >
                            {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '0.5rem',
                      },
                    }}
                  />
                </Grid>
                {errors.general && (
                  <Grid size={12}>
                    <Typography color="error" variant="body2">
                      {errors.general}
                    </Typography>
                  </Grid>
                )}
                <Grid size={12} sx={{ textAlign: 'right' }}>
                  <Link
                    component={RouterLink}
                    to="/forgot-password"
                    variant="body2"
                    underline="hover"
                    onClick={() => analytics.forgotPasswordClick()}
                    sx={{ color: 'primary.main' }}
                  >
                    Forgot password?
                  </Link>
                </Grid>
                <Grid size={12}>
                  <Button
                    type="submit"
                    fullWidth
                    variant="gradient"
                    size="large"
                    disabled={isSubmitting}
                    sx={{
                      py: 1.5,
                    }}
                  >
                    {isSubmitting ? <CircularProgress size={24} color="inherit" /> : 'Log In'}
                  </Button>
                </Grid>
              </Grid>
            </form>
          </Paper>

          <Box sx={{ mt: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Don't have an account?{' '}
              <Link
                component={RouterLink}
                to="/signup"
                variant="body2"
                underline="hover"
                sx={{ fontWeight: 600, color: 'primary.main' }}
              >
                Sign up
              </Link>
            </Typography>
          </Box>
        </Container>
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <MuiAlert
            elevation={6}
            variant="filled"
            severity={snackbar.severity}
            onClose={handleSnackbarClose}
            sx={{ width: '100%', mt: 8 }}
          >
            {snackbar.message}
          </MuiAlert>
        </Snackbar>
      </Box>
    </Box>
  );
};

export default Login;
